<template>
  <template v-if="showChatBot">
    <a-float-button type="primary" @click="chatBotOpen = true">
      <template #icon>
        <icon-vue class="h-6 w-6 text-white -transform-translate-x-[3px]" icon="bx:bot" />
      </template>
    </a-float-button>
    <!-- 右侧抽屉：从导航栏下方开始、全高、宽度可拖拽 -->
    <a-drawer
      v-model:open="chatBotOpen"
      placement="right"
      :width="chatBotWidth"
      :header-style="{ padding: '8px 16px' }"
      :body-style="{ padding: '0', overflow: 'hidden' }"
      :root-style="{ top: NAV_H, height: `calc(100% - ${NAV_H})` }"
    >
      <template #title>
        <div class="dw-title">
          <div class="dw-badge">
            <icon-vue icon="lucide:bot-message-square" />
          </div>
          <div class="dw-who">
            <b>CTC-AI-Robot</b>
            <span>
              <i class="dw-dot"></i>
              在线 · 查数据 / 提需求
            </span>
          </div>
        </div>
      </template>
      <div class="relative h-full w-full">
        <!-- 左边缘拖拽手柄：按住左右拖动改变抽屉宽度 -->
        <div class="resize-handle" title="拖动调整宽度" @mousedown="startResize"></div>
        <chat-content />
      </div>
    </a-drawer>
  </template>
</template>

<script setup lang="ts">
import appUtil from '@/utils/app';
import { Icon as IconVue } from '@iconify/vue';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import ChatContent from './chat-content.vue';
import { chatBotOpen, chatBotWidth } from './use-chat-bot';

// 顶部导航栏高度（header-menu-layout 的 header 为 55px）；抽屉从其下方开始，不遮挡导航
const NAV_H = '55px';
const MIN_W = 320;
const showChatBot = ref(false);
// open / 宽度改用全局单例（use-chat-bot），供「提需求」等页面按钮外部打开并指定宽度

const maxW = () => Math.round(window.innerWidth * 0.95);
const clamp = (w: number) => Math.max(MIN_W, Math.min(maxW(), w));

// —— 拖拽调宽（右侧抽屉，拖左边缘：往左变宽、往右变窄）——
let startX = 0;
let startW = 0;
const onDragMove = (e: MouseEvent) => {
  chatBotWidth.value = clamp(startW + (startX - e.clientX));
};
const stopResize = () => {
  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('mouseup', stopResize);
  document.body.style.userSelect = '';
};
const startResize = (e: MouseEvent) => {
  startX = e.clientX;
  startW = chatBotWidth.value;
  document.body.style.userSelect = 'none';
  document.addEventListener('mousemove', onDragMove);
  document.addEventListener('mouseup', stopResize);
};

const onResize = () => {
  chatBotWidth.value = clamp(chatBotWidth.value);
};

const getChatBotIsShow = () => {
  if (appUtil.isMobile()) {
    return false;
  }
  return true;
};

onMounted(() => {
  showChatBot.value = getChatBotIsShow();
  // 默认展开宽度 = 窗口 50%（受 [MIN_W, 95vw] 收敛）；用户仍可拖拽左边缘调整
  chatBotWidth.value = clamp(Math.round(window.innerWidth * 0.5));
  window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
  stopResize();
});
</script>

<style scoped>
.dw-title {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dw-badge {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  flex: none;
  background: linear-gradient(135deg, #1677ff, #52a0ff);
  color: #fff;
  display: grid;
  place-items: center;
}
.dw-badge :deep(svg) {
  width: 17px;
  height: 17px;
}
.dw-who {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.dw-who b {
  font-size: 14px;
  font-weight: 600;
}
.dw-who span {
  font-size: 11px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 400;
}
.dw-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #16a34a;
  display: inline-block;
}
.resize-handle {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 6px;
  cursor: col-resize;
  z-index: 20;
  background-color: transparent;
  transition: background-color 0.15s;
}
.resize-handle:hover {
  background-color: #1890ff33;
}
</style>
